import React, { useMemo } from 'react'
import { Box } from 'webapp/components/common/fundamentals'
import { Property } from 'webapp/components/elements/Property/Property'
import {
  RegisterItem,
  RegisterItemFlatPropertyData,
} from '@core/entities/register/RegisterItem'
import { RegisterItemPropertyValue } from '@modules/registers/propertyValue/RegisterItemPropertyValue'
import { Divider } from '@mui/material'
import { localizedStrings } from '@core/strings'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem/types'

type RegisterItemDetailsPropertiesProps = {
  registerItem: RegisterItem
  excludedProperties?: Array<RegisterItemFlatPropertyData['type']>
  horizontal?: boolean
  variant?: 'details' | 'table'
  onChangeTab?: (tab: string) => void
  enableScheduleInspections?: boolean
}

export const RegisterItemDetailsProperties: React.FC<
  RegisterItemDetailsPropertiesProps
> = ({
  registerItem,
  excludedProperties = [],
  horizontal = true,
  variant = 'details',
  onChangeTab,
  enableScheduleInspections = false,
}) => {
  const memoisedUniqueTableColumns: RegisterItemFlatPropertyData[] =
    useMemo(() => {
      const nameCounts = new Map<string, number>()

      const uniqueColumnNames = registerItem.data?.map((column) => {
        const currentCount = nameCounts.get(column.name) || 0
        const columnName = `${column.name}${
          currentCount ? ` (${currentCount})` : ''
        }`

        // Update the count in the map for the next occurrence
        nameCounts.set(column.name, currentCount + 1)

        const columnValue =
          registerItem.getRegisterItemPropertyDisplayValues(column)

        return { ...column, name: columnName, value: columnValue }
      })

      return uniqueColumnNames
    }, [registerItem.data])

  return (
    <Box>
      {memoisedUniqueTableColumns?.map(
        (item: RegisterItemFlatPropertyData & { value: string }) => {
          if (excludedProperties.includes(item.type)) return null
          // add a divider at the first inspection item in this array

          const isInspectionColumn = item.type === 'INSPECTION'
          const inspectionColumns = memoisedUniqueTableColumns.filter(
            (column) => column.type === 'INSPECTION',
          )
          const inspectionIndex = inspectionColumns.findIndex(
            (col: RegisterItemInspectionProperty<string>) =>
              col.registerPropertyId === item.registerPropertyId,
          )

          const isFirstInspection = isInspectionColumn && inspectionIndex === 0
          const hideInspectionColumn =
            isInspectionColumn &&
            inspectionIndex > 0 &&
            enableScheduleInspections

          if (hideInspectionColumn) return null

          return (
            <Box key={item.registerPropertyId}>
              {(isFirstInspection ||
                ['PERMIT', 'TASK'].includes(item.type)) && (
                <Divider sx={{ my: 2 }} />
              )}
              <Property
                sx={{
                  mb: 1.5,
                }}
                gap={1}
                horizontal={
                  item.type === 'INSPECTION' && !enableScheduleInspections
                    ? false
                    : horizontal
                }
                heading={
                  item.type === 'INSPECTION'
                    ? isFirstInspection
                      ? localizedStrings.inspections
                      : null
                    : item.name
                }
                subHeadingComponent={
                  <RegisterItemPropertyValue
                    variant={variant}
                    registerItemPropertyData={item}
                    registerItem={registerItem}
                    onChangeTab={onChangeTab}
                  />
                }
              />
            </Box>
          )
        },
      )}
    </Box>
  )
}
